/**
 * R7/D — Single source of truth for the six persona-landing Category buckets.
 *
 * Named `PersonaCategory` (not just `Category`) to avoid collision with the
 * legacy `Category` type in `mock-listings.data.ts`, which is a different
 * 5-value set (`vineyard | farm | brewery | attraction | offgrid`). The queued
 * server-side taxonomy pass will reconcile the two — until then, both coexist.
 *
 * Colors match `tailwind.config.js` under `theme.extend.colors.category.*`,
 * so `bg-category-farm`, `text-category-winery`, etc. resolve to the same hex.
 */

export type PersonaCategory =
  | 'farm'
  | 'winery'
  | 'brewery'
  | 'driveway'
  | 'attraction'
  | 'nature';

export interface IPersonaCategoryMeta {
  slug: PersonaCategory;
  label: string;
  color: string;            // Raw hex (for anything that can't consume Tailwind tokens)
  tailwindBg: string;       // e.g. 'bg-category-farm'
  tailwindText: string;     // e.g. 'text-category-farm'
  icon: string;             // Material Symbols name
  personaRoute: string;     // e.g. '/farm'
  heroImage: string;        // Persona OG / hero fallback
}

export const PERSONA_CATEGORY_META: Record<PersonaCategory, IPersonaCategoryMeta> = {
  farm: {
    slug: 'farm',
    label: 'Farms',
    color: '#DE5260',
    tailwindBg: 'bg-category-farm',
    tailwindText: 'text-category-farm',
    icon: 'agriculture',
    personaRoute: '/farm',
    heroImage: 'assets/images/host_farm.webp',
  },
  winery: {
    slug: 'winery',
    label: 'Wineries',
    color: '#A541A1',
    tailwindBg: 'bg-category-winery',
    tailwindText: 'text-category-winery',
    icon: 'wine_bar',
    personaRoute: '/winery',
    heroImage: 'assets/images/host_vineyard.webp',
  },
  brewery: {
    slug: 'brewery',
    label: 'Breweries',
    color: '#E8A23D',
    tailwindBg: 'bg-category-brewery',
    tailwindText: 'text-category-brewery',
    icon: 'sports_bar',
    personaRoute: '/brewery',
    heroImage: 'assets/images/host_brewery.webp',
  },
  driveway: {
    slug: 'driveway',
    label: 'Driveways',
    color: '#42B0AF',
    tailwindBg: 'bg-category-driveway',
    tailwindText: 'text-category-driveway',
    icon: 'home_work',
    personaRoute: '/driveway',
    heroImage: 'assets/images/host_village.webp',
  },
  attraction: {
    slug: 'attraction',
    label: 'Attractions',
    color: '#3B7B7A',
    tailwindBg: 'bg-category-attraction',
    tailwindText: 'text-category-attraction',
    icon: 'attractions',
    personaRoute: '/attraction',
    heroImage: 'assets/images/host_museum.webp',
  },
  nature: {
    slug: 'nature',
    label: 'Nature',
    color: '#295d42',
    tailwindBg: 'bg-category-nature',
    tailwindText: 'text-category-nature',
    icon: 'nature',
    personaRoute: '/land',
    heroImage: 'assets/images/host_opportunity.webp',
  },
};

export function getPersonaCategoryMeta(slug: PersonaCategory): IPersonaCategoryMeta {
  return PERSONA_CATEGORY_META[slug];
}

